<script lang="ts">
  import { fade } from 'svelte/transition';
  import { getStoresContext, type StoresKey } from "~/stores";
  import { onMount } from 'svelte';
  import SearchInput_impl from '../search/input/SearchInput.svelte';
  import { currentDayHours } from '~/theme/demo2025/stores/scOpeningHours';
  import { type Alternate } from "~/metadata";
  import { ReverseRouter } from "~/reverse-router";

  export let request_store_key: StoresKey;
  export let logo: { src: string; alt: string };
  export let navigationLinks: any[];
  export let allNavigationLinks: any[] = [];
  export let secondaryNavigationLinks: any[] = [];
  export let homePageUrl: string;
  export let enableHeaderLanguageSelector: boolean = false;
  export let enableHeaderLanguageSelectorMobile: boolean = false;
  export let enableOverlayLanguageSelector: boolean = true;
  export let maxLanguagesInline: string = "3";
  export let languageDropdownMinWidth: string = "100px";
  export let languageDropdownFontSize: string = "1rem";
  export let languageDropdownFontWeight: string = "500";
  export let enableOverlayMenu: boolean = true;
  export let enableOverlayMenuDesktop: boolean = true;
  export let enableOverlayMenuMobile: boolean = true;
  export let enableHeaderDropdowns: boolean = true;

  // Search overlay config props
  export let searchOverlayBackgroundColor: string = "#FFFFFF";
  export let searchInputBorderColor: string = "#E0E0E0";
  export let searchInputBorderColorFocused: string = "#424242";
  export let searchInputTextColor: string = "#424242";
  export let searchInputPlaceholderColor: string = "#9E9E9E";
  export let searchInputIconColor: string = "#424242";
  export let searchResultsSectionHeaderBorderColor: string = "#EFEFEF";
  export let searchResultsSectionHeaderBorderThickness: string = "1px";

  let _class = "";
  export { _class as class };

  // Dropdown state management
  let openDropdownIndex: number | null = null;
  let openSubDropdownIndex: number | null = null;
  const stores_context = getStoresContext(request_store_key);
  const { locale, alternate, reverse_router, site } = stores_context;

  // Language mapping for display
  const languageLabels: Record<string, string> = {
    'fi-FI': 'FI',
    'en-US': 'EN',
    'sv-SE': 'SV',
    'ru-RU': 'RU',
    'et-EE': 'ET',
    'nb-NO': 'NO',
    'da-DK': 'DA'
  };

  // Full language names for dropdown
  const languageNames: Record<string, string> = {
    'fi-FI': 'Finnish',
    'en-US': 'English',
    'sv-SE': 'Swedish',
    'ru-RU': 'Russian',
    'et-EE': 'Estonian',
    'nb-NO': 'Norwegian',
    'da-DK': 'Danish'
  };

  // Get supported languages from the site context
  $: supportedLanguages = $site?.supportedLanguages || [];
  
  // Parse maxLanguagesInline and determine if we should use dropdown
  $: maxLanguagesThreshold = parseInt(maxLanguagesInline) || 3;
  $: shouldUseDropdown = supportedLanguages.length > maxLanguagesThreshold;
  $: currentLanguageLabel = languageLabels[$locale] || $locale?.slice(0, 2).toUpperCase();

  // Function to get href for a locale using alternate or reverse router
  function hrefFor(
    locale: string, 
    alternate: readonly Alternate[], 
    reverse_router: Readonly<ReverseRouter>
  ) {
    const alt = alternate.find(x => x.locale == locale);
    return alt ? alt.href : reverse_router.findPurposeRoute('home page', locale);
  }

  let isSearchOpen = false;
  let searchOverlay: HTMLElement;
  let searchInputRef: HTMLInputElement;
  let isMobileView = false;
  let isLanguageDropdownOpen = false;
  let languageDropdownRef: HTMLElement | undefined;

  // Dropdown event handlers
  function handleMouseEnter(idx: number) {
    if (!isMobileView) openDropdownIndex = idx;
  }
  function handleMouseLeave() {
    if (!isMobileView) openDropdownIndex = null;
  }
  function handleSubMouseEnter(idx: number) {
    if (!isMobileView) openSubDropdownIndex = idx;
  }
  function handleSubMouseLeave() {
    if (!isMobileView) openSubDropdownIndex = null;
  }
  let navLinksElement: HTMLElement;
  let navbarElement: HTMLElement;
  let logoElement: HTMLElement;
  let actionsElement: HTMLElement;
  let headerElement: HTMLElement;
  let currentFontSize: number | null = null;
  let isAdjustingFontSize = false;

  const browser = typeof window !== 'undefined';
  let currentPath = '';

  $: if (browser) {
    currentPath = window.location.pathname;
    isMobileView = window.innerWidth < 992;
  }

  function toggleSearch(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    
    if (isSearchOpen) {
      closeSearch();
    } else {
      if (isOverlayMenuOpen) {
        isOverlayMenuOpen = false;
      }
      isSearchOpen = true;
      // Focus the input after a small delay to ensure the element is mounted
      setTimeout(() => {
        searchInputRef?.focus();
      }, 0);
    }
  }

  let isOverlayMenuOpen = false;

  function toggleMobileMenu() {
    if (isSearchOpen) {
      closeSearch();
    }
    isOverlayMenuOpen = !isOverlayMenuOpen;
  }

  function closeSearch() {
    isSearchOpen = false;
    isOverlayMenuOpen = false;
  }

  function closeMenu() {
    isOverlayMenuOpen = false;
  }

  function toggleLanguageDropdown(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    
    if (isLanguageDropdownOpen) {
      closeLanguageDropdown();
    } else {
      // Close overlay menu when opening language dropdown
      if (isOverlayMenuOpen) {
        isOverlayMenuOpen = false;
      }
      isLanguageDropdownOpen = true;
    }
  }

  function closeLanguageDropdown() {
    isLanguageDropdownOpen = false;
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (isSearchOpen) {
        closeSearch();
      } else if (isLanguageDropdownOpen) {
        closeLanguageDropdown();
      } else if (isOverlayMenuOpen) {
        closeMenu();
      }
    }

    // Handle Enter and Space key presses for non-interactive elements with click handlers
    if ((event.key === 'Enter' || event.key === ' ') && event.target) {
      const target = event.target as HTMLElement;
      
      // For search backdrop
      if (target.classList.contains('search-backdrop')) {
        event.preventDefault();
        closeSearch();
      }
      
      // For overlay backdrop
      if (target.classList.contains('overlay-backdrop')) {
        event.preventDefault();
        closeMenu();
      }
    }
  }

  // Function to check if nav items are overflowing and adjust font size accordingly
  function adjustNavFontSize() {
    if (!browser || isMobileView || !navLinksElement || !navbarElement || !logoElement || !actionsElement || isAdjustingFontSize) {
      return;
    }
    
    isAdjustingFontSize = true;

    const computedStyle = getComputedStyle(navLinksElement.querySelector('.nav-item a') as HTMLElement);
    const originalFontSizeStr = computedStyle.getPropertyValue('--nav-link-font-size');
    const originalFontSize = parseFloat(originalFontSizeStr);
    
    if (isNaN(originalFontSize)) {
      isAdjustingFontSize = false;
      return;
    }
    
    if (currentFontSize === null) {
      currentFontSize = originalFontSize;
    }
    
    // Calculate available width for nav links
    const navbarWidth = navbarElement.offsetWidth;
    const logoWidth = logoElement.offsetWidth;
    const actionsWidth = actionsElement.offsetWidth;
    const paddingBuffer = 60;
    const availableWidth = navbarWidth - logoWidth - actionsWidth - paddingBuffer;
    
    // Get the original gap between nav items
    const originalGapStr = getComputedStyle(navLinksElement).getPropertyValue('--nav-link-gap');
    const originalGap = parseFloat(originalGapStr);
    const currentGapStr = navLinksElement.style.getPropertyValue('--adjusted-gap') || originalGapStr;
    const currentGap = parseFloat(currentGapStr) || originalGap;
    
    // Get total width of all nav items at current font size
    const navItemsWidth = Array.from(navLinksElement.querySelectorAll('.nav-item')).reduce((total, item) => {
      return total + (item as HTMLElement).offsetWidth;
    }, 0);
    
    // Calculate total width with gaps
    const totalGapWidth = (navLinksElement.querySelectorAll('.nav-item').length - 1) * currentGap;
    const totalNavWidth = navItemsWidth + totalGapWidth;
    
    const viewportWidth = window.innerWidth;
    
    // Check if nav items are overflowing or close to overflowing
    // More conservative threshold (95% of available space)
    if (totalNavWidth > availableWidth * 0.95) {
      // First try reducing the gap before reducing font size
      if (currentGap > 10) {
        const newGap = currentGap > 30 ? currentGap - 10 : currentGap - 5;
        navLinksElement.style.setProperty('--adjusted-gap', `${Math.max(newGap, 10)}px`);
      } else {
        // If gap is already at minimum, reduce font size
        // Scale reduction factor based on screen size and overflow amount
        let reductionFactor = 0.2; // Default reduction
        
        // For larger screens or more significant overflow, increase reduction factor
        if (viewportWidth > 1200) {
          reductionFactor = 0.5; // More aggressive for large screens
        } else if (viewportWidth > 992) {
          reductionFactor = 0.3; // Moderately aggressive for medium screens
        }
        
        // If we're severely overflowing, increase reduction further
        if (totalNavWidth > availableWidth * 1.1) {
          reductionFactor += 0.2;
        }
        
        let newFontSize = currentFontSize - reductionFactor;
        
        // Don't go below a minimum readable font size
        if (newFontSize < 10) {
          newFontSize = 10;
        }
        
        // Apply the new font size
        navLinksElement.style.setProperty('--adjusted-font-size', `${newFontSize}px`);
        currentFontSize = newFontSize;
      }
    } else if (totalNavWidth < availableWidth * 0.85) {
      // Only increase if we have plenty of space (using a conservative threshold)
      if (currentGap < originalGap) {
        // Increase gap first, up to original, but do it gradually
        const newGap = Math.min(currentGap + 3, originalGap);
        navLinksElement.style.setProperty('--adjusted-gap', `${newGap}px`);
      } else if (currentFontSize < originalFontSize) {
        // Then increase font size if needed, veery gradually
        let newFontSize = currentFontSize + 0.1;
        
        // Don't exceed the original font size
        if (newFontSize > originalFontSize) {
          newFontSize = originalFontSize;
        }
        
        // Apply the new font size
        navLinksElement.style.setProperty('--adjusted-font-size', `${newFontSize}px`);
        currentFontSize = newFontSize;
      }
    }
    
    isAdjustingFontSize = false;
  }

  $: if (browser && headerElement) {
    const headerHeight = headerElement.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  }

  $: if (browser) {
    if (isSearchOpen) {
      document.documentElement.classList.add('demo2025-search-open');
    } else {
      document.documentElement.classList.remove('demo2025-search-open');
    }

    if (isOverlayMenuOpen) {
      document.documentElement.classList.add('demo2025-menu-open');
    } else {
      document.documentElement.classList.remove('demo2025-menu-open');
    }
  }

  onMount(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSearchOpen && searchOverlay && !searchOverlay.contains(event.target as Node)) {
        const searchButton = document.querySelector('.search-toggle');
        if (!searchButton?.contains(event.target as Node)) {
          isSearchOpen = false;
        }
      }
      
      if (isLanguageDropdownOpen && languageDropdownRef && !languageDropdownRef.contains(event.target as Node)) {
        isLanguageDropdownOpen = false;
      }
    };

    const handleResize = () => {
      if (browser) {
        isMobileView = window.innerWidth < 992;
        
        // Reset font size on mobile view
        if (isMobileView && navLinksElement) {
          navLinksElement.style.removeProperty('--adjusted-font-size');
          navLinksElement.style.removeProperty('--adjusted-gap');
          currentFontSize = null;
        } else {
          setTimeout(adjustNavFontSize, 100);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', handleResize);
    
    handleResize();
    
    // Run font size adjustment after initial render
    setTimeout(adjustNavFontSize, 100);
    
    // Set up a ResizeObserver to detect when the navbar or its contents change size
    if (browser && window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        if (!isMobileView) {
          adjustNavFontSize();
        }
      });
      
      if (navbarElement) {
        resizeObserver.observe(navbarElement);
      }
      if (logoElement) {
        resizeObserver.observe(logoElement);
      }
      if (actionsElement) {
        resizeObserver.observe(actionsElement);
      }
      if (navLinksElement) {
        resizeObserver.observe(navLinksElement);
        navLinksElement.querySelectorAll('.nav-item').forEach(item => {
          resizeObserver.observe(item);
        });
      }
      
      return () => {
        resizeObserver.disconnect();
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleKeydown);
        window.removeEventListener('resize', handleResize);
        if (browser) {
          document.documentElement.classList.remove('demo2025-search-open');
          document.documentElement.classList.remove('demo2025-menu-open');
        }
      };
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('resize', handleResize);
      if (browser) {
        document.documentElement.classList.remove('demo2025-search-open');
        document.documentElement.classList.remove('demo2025-menu-open');
      }
    };
  });
</script>


<header class={_class} bind:this={headerElement}>
  <nav class="navbar" class:menu-open={isOverlayMenuOpen} bind:this={navbarElement}>
    <div class="navbar-logo" bind:this={logoElement}>
      <a href={homePageUrl}>
        <img src={logo.src} alt={logo.alt} />
      </a>
    </div>

    <!-- Desktop nav links -->
    <ul class="navbar-links" bind:this={navLinksElement}>
      {#each navigationLinks as link, idx}
        <li class="nav-item"
            class:has-dropdown={enableHeaderDropdowns && link.children && link.children.length > 0}
            on:mouseenter={() => enableHeaderDropdowns && handleMouseEnter(idx)}
            on:mouseleave={() => enableHeaderDropdowns && handleMouseLeave()}
        >
          <a 
            href={link.href} 
            class:active={browser && link.href === currentPath}
          >
            {link.text}
          </a>
          {#if enableHeaderDropdowns && link.children && link.children.length > 0 && openDropdownIndex === idx}
            <ul class="header-dropdown-menu">
              {#each link.children as child, cidx}
                <li class="header-dropdown-navitem"
                    on:mouseenter={() => handleSubMouseEnter(cidx)}
                    on:mouseleave={handleSubMouseLeave}
                >
                  <a href={child.href} class:has-children={child.children && child.children.length > 0}>
                    {child.text}
                  </a>
                  {#if child.children && child.children.length > 0 && openSubDropdownIndex === cidx}
                    <ul class="header-dropdown-menu header-dropdown-submenu">
                      {#each child.children as subchild}
                        <li class="header-dropdown-navitem">
                          <a href={subchild.href}>{subchild.text}</a>
                        </li>
                      {/each}
                    </ul>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        </li>
      {/each}
    </ul>

    <!-- Desktop header actions -->
    <div class="desktop-header-actions" bind:this={actionsElement}>
      <!-- Dropped fragments slot -->
      <slot name="dropped-fragments" />

      {#if enableHeaderLanguageSelector && supportedLanguages.length > 1}
        <div class="header-language-selector" bind:this={languageDropdownRef}>
          {#if shouldUseDropdown}
            <!-- Dropdown mode -->
            <button 
              class="language-dropdown-toggle" 
              on:click={toggleLanguageDropdown}
              aria-label="Select language"
              aria-expanded={isLanguageDropdownOpen}
            >
              {currentLanguageLabel}
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            {#if isLanguageDropdownOpen}
              <div class="language-dropdown" transition:fade={{ duration: 150 }}>
                {#each supportedLanguages as langCode}
                  {#if langCode === $locale}
                    <span class="language-option active">
                      {languageNames[langCode] || langCode}
                    </span>
                  {:else}
                    <a 
                      class="language-option" 
                      href={hrefFor(langCode, $alternate, $reverse_router)}
                      on:click={closeLanguageDropdown}
                      aria-label={`Switch to ${languageNames[langCode] || langCode}`}
                    >
                      {languageNames[langCode] || langCode}
                    </a>
                  {/if}
                {/each}
              </div>
            {/if}
          {:else}
            <!-- Inline mode -->
            {#each supportedLanguages as langCode}
              {#if langCode === $locale}
                <span class="language-toggle active">
                  {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                </span>
              {:else}
                <a 
                  class="language-toggle" 
                  href={hrefFor(langCode, $alternate, $reverse_router)}
                  aria-label={`Switch to ${langCode}`}
                >
                  {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                </a>
              {/if}
            {/each}
          {/if}
        </div>
      {/if}
      
      <button class="search-toggle" on:click={toggleSearch} aria-label="Toggle search">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      </button>
      {#if enableOverlayMenu && enableOverlayMenuDesktop}
        <button 
          class="hamburger" 
          on:click={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={isOverlayMenuOpen}
        >
          <span class:open={isOverlayMenuOpen}></span>
        </button>
      {/if}
    </div>

    <!-- Mobile header actions -->
    <div class="mobile-header-actions">

      <!-- Dropped fragments slot for mobile -->
      <slot name="dropped-fragments" />

      {#if enableHeaderLanguageSelector && enableHeaderLanguageSelectorMobile && supportedLanguages.length > 1}
        <div class="header-language-selector">
          {#if shouldUseDropdown}
            <!-- Dropdown mode -->
            <button 
              class="language-dropdown-toggle" 
              on:click={toggleLanguageDropdown}
              aria-label="Select language"
              aria-expanded={isLanguageDropdownOpen}
            >
              {currentLanguageLabel}
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            {#if isLanguageDropdownOpen}
              <div class="language-dropdown" transition:fade={{ duration: 150 }}>
                {#each supportedLanguages as langCode}
                  {#if langCode === $locale}
                    <span class="language-option active">
                      {languageNames[langCode] || langCode}
                    </span>
                  {:else}
                    <a 
                      class="language-option" 
                      href={hrefFor(langCode, $alternate, $reverse_router)}
                      on:click={closeLanguageDropdown}
                      aria-label={`Switch to ${languageNames[langCode] || langCode}`}
                    >
                      {languageNames[langCode] || langCode}
                    </a>
                  {/if}
                {/each}
              </div>
            {/if}
          {:else}
            <!-- Inline mode -->
            {#each supportedLanguages as langCode}
              {#if langCode === $locale}
                <span class="language-toggle active">
                  {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                </span>
              {:else}
                <a 
                  class="language-toggle" 
                  href={hrefFor(langCode, $alternate, $reverse_router)}
                  aria-label={`Switch to ${langCode}`}
                >
                  {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                </a>
              {/if}
            {/each}
          {/if}
        </div>
      {/if}
      
      <button class="search-toggle" on:click={toggleSearch} aria-label="Toggle search">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      </button>
      {#if enableOverlayMenu && enableOverlayMenuMobile}
        <button 
          class="hamburger" 
          on:click={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={isOverlayMenuOpen}
        >
          <span class:open={isOverlayMenuOpen}></span>
        </button>
      {/if}
    </div>
  </nav>

  {#if isSearchOpen}
    <div 
      class="search-overlay" 
      bind:this={searchOverlay}
      role="dialog"
      aria-label="Search"
    >
      <div 
        class="search-backdrop" 
        on:click={closeSearch} 
        on:keydown={(e) => e.key === 'Enter' || e.key === ' ' ? closeSearch() : null}
        role="button" 
        tabindex="0"
      ></div>
      <div class="search-container">
        <SearchInput_impl
          {request_store_key}
          isOpen={isSearchOpen}
          {closeSearch}
          bind:inputRef={searchInputRef}
          searchOverlayBackgroundColor={searchOverlayBackgroundColor}
          searchInputBorderColor={searchInputBorderColor}
          searchInputBorderColorFocused={searchInputBorderColorFocused}
          searchInputTextColor={searchInputTextColor}
          searchInputPlaceholderColor={searchInputPlaceholderColor}
          searchInputIconColor={searchInputIconColor}
        />
      </div>
    </div>
  {/if}
</header>


{#if isOverlayMenuOpen}
  <!-- Overlay backdrop - only shown on desktop -->
  <button 
    class="overlay-backdrop" 
    on:click={closeMenu} 
    aria-label="Close menu"
    transition:fade={{ duration: 150 }}
  ></button>
  
  <!-- Menu with conditional transitions based on viewport size -->
  {#if isMobileView}
    <div class="overlay-menu mobile" transition:fade={{ duration: 150 }}>
      <div class="overlay-menu-content">
        <div class="overlay-nav">
          <!-- Primary Navigation Links - show full menu -->
          <ul class="overlay-nav-links">
            {#each allNavigationLinks as link}
              <li class="overlay-nav-item" class:submenu-item={link.isSubmenuItem}>
                <a 
                  href={link.href}
                  on:click={() => isOverlayMenuOpen = false}
                  class:active={browser && link.href === currentPath}
                >
                  {link.text}
                </a>
              </li>
            {/each}
          </ul>
          
          <!-- Secondary Navigation Links -->
          {#if secondaryNavigationLinks.length > 0}
            <div class="secondary-nav-divider"></div>
            <ul class="overlay-nav-links secondary">
              {#each secondaryNavigationLinks as link}
                <li class="overlay-nav-item secondary">
                  <a 
                    href={link.href}
                    on:click={() => isOverlayMenuOpen = false}
                    class:active={browser && link.href === currentPath}
                  >
                    {link.text}
                  </a>
                </li>
              {/each}
            </ul>
          {/if}
        </div>

        <div class="overlay-footer">
          <div class="today-hours">
            {$currentDayHours}
          </div>
          {#if enableOverlayLanguageSelector}
            <div class="language-selector">
              {#if supportedLanguages.length > 1}
                {#each supportedLanguages as langCode}
                  {#if langCode === $locale}
                    <span class="language-toggle active">
                      {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                    </span>
                  {:else}
                    <a 
                      class="language-toggle" 
                      href={hrefFor(langCode, $alternate, $reverse_router)}
                      on:click={() => isOverlayMenuOpen = false}
                      aria-label={`Switch to ${langCode}`}
                    >
                      {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                    </a>
                  {/if}
                {/each}
              {/if}
            </div>
          {/if}
        </div>
      </div>
    </div>
  {:else}
    <div 
      class="overlay-menu desktop" 
      transition:fade={{ duration: 150 }}
    >
      <div class="overlay-menu-content">
        <!-- Desktop menu content -->
        <div class="overlay-nav">
          <!-- Primary Navigation Links - show full menu -->
          <ul class="overlay-nav-links">
            {#each allNavigationLinks as link}
              <li class="overlay-nav-item" class:submenu-item={link.isSubmenuItem}>
                <a 
                  href={link.href}
                  on:click={() => isOverlayMenuOpen = false}
                  class:active={browser && link.href === currentPath}
                >
                  {link.text}
                </a>
              </li>
            {/each}
          </ul>
          
          <!-- Secondary Navigation Links -->
          {#if secondaryNavigationLinks.length > 0}
            <div class="secondary-nav-divider"></div>
            <ul class="overlay-nav-links secondary">
              {#each secondaryNavigationLinks as link}
                <li class="overlay-nav-item secondary">
                  <a 
                    href={link.href}
                    on:click={() => isOverlayMenuOpen = false}
                    class:active={browser && link.href === currentPath}
                  >
                    {link.text}
                  </a>
                </li>
              {/each}
            </ul>
          {/if}
        </div>

        <div class="overlay-footer">
          <div class="today-hours">
            {$currentDayHours}
          </div>
          {#if enableOverlayLanguageSelector}
            <div class="language-selector">
              {#if supportedLanguages.length > 1}
                {#each supportedLanguages as langCode}
                  {#if langCode === $locale}
                    <span class="language-toggle active">
                      {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                    </span>
                  {:else}
                    <a 
                      class="language-toggle" 
                      href={hrefFor(langCode, $alternate, $reverse_router)}
                      on:click={() => isOverlayMenuOpen = false}
                      aria-label={`Switch to ${langCode}`}
                    >
                      {languageLabels[langCode] || langCode.slice(0, 2).toUpperCase()}
                    </a>
                  {/if}
                {/each}
              {/if}
            </div>
          {/if}
        </div>
      </div>
    </div>
  {/if}
{/if}

<style lang="scss">
  header {
    width: 100%;
    position: relative;
    height: auto;
    background-color: var(--header-background-color);
    max-width: 1920px;
    margin: 0 auto;
    border-bottom: var(--header-border-bottom-thickness) solid var(--header-border-bottom-color);
    font-family: var(--liferay-font-family-sans-serif);
    padding: var(--header-padding) 0;
  }

  .navbar {
    width: 100%;
    margin: 0 auto;
    padding: 0 1rem;
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 50;
    background-color: var(--header-background-color);

    .navbar-logo {
      display: flex;
      align-items: center;
      width: 100%;
      max-width: var(--logo-max-width, 128px);
      min-width: var(--logo-min-width, 103px);
      
      a {
        display: block;
        width: 100%;
        height: 100%;

        img {
          object-fit: contain;
          width: 100%;
          height: auto;
        }
      }
    }

    .navbar-links {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      list-style: none;
      margin: 0;
      padding: 0;
      padding-left: 1rem;
      gap: var(--adjusted-gap, var(--nav-link-gap));
      flex-wrap: nowrap;
      
      @media screen and (max-width: 991px) {
        display: none !important;
      }

      .nav-item {
        position: relative;
        
        a {
          color: var(--nav-link-color);
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          transition: opacity 0.2s ease;
          font-size: var(--adjusted-font-size, var(--nav-link-font-size));
          font-weight: 600;
          line-height: 28px;
          position: relative;
          white-space: nowrap;

          &.active {
            color: var(--nav-link-active-color);
            text-decoration: underline;
            text-underline-offset: var(--nav-link-underline-offset);
            text-decoration-thickness: var(--nav-link-underline-thickness);
            text-decoration-color: var(--nav-link-underline-color);
          }

          &:hover {
            color: var(--nav-link-hover-color);
            opacity: 0.7;
          }

          &.has-children {
            padding-right: 1.2em;
          }
        }
      }

      /* Header Dropdown Menu Styles */
      .header-dropdown-menu {
        position: absolute;
        top: 100%;
        left: 0;
        background: var(--dropdown-background-color, #fff);
        border: 1px solid var(--dropdown-border-color, #e5e5e5);
        box-shadow: var(--dropdown-shadow, 0 4px 6px rgba(0,0,0,0.1));
        padding: var(--dropdown-padding-vertical, 1rem) var(--dropdown-padding-horizontal, 1rem);
        min-width: var(--dropdown-min-width, 180px);
        max-width: var(--dropdown-max-width, 300px);
        z-index: 200;
        font-size: var(--dropdown-item-font-size, 14px);
        font-weight: var(--dropdown-item-font-weight, 400);
        list-style: none;
        margin: 0;
        border-radius: 4px;
      }

      .header-dropdown-submenu {
        left: 100%;
        top: 0;
        margin-left: 2px;
      }

      .header-dropdown-navitem {
        position: relative;
        
        a {
          display: block;
          padding: var(--dropdown-item-padding-vertical, 0.5rem) var(--dropdown-item-padding-horizontal, 1rem);
          color: var(--nav-link-color, #424242);
          text-decoration: none;
          white-space: nowrap;
          transition: all 0.2s ease;
          text-transform: none;
          letter-spacing: normal;
          font-size: var(--dropdown-item-font-size, 14px);
          font-weight: var(--dropdown-item-font-weight, 400);

          &:hover {
            color: var(--dropdown-item-hover-color, #202020);
            background: var(--dropdown-item-hover-bg-color, rgba(0,0,0,0.05));
          }
        }
      }
    }

          .desktop-header-actions {
        display: none;
        align-items: center;
        gap: 1rem;
        margin-left: auto;

        @media screen and (min-width: 992px) {
          display: flex;
        }

        .header-language-selector {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-right: 0.5rem;
          position: relative;

          .language-toggle {
            background: none;
            border: none;
            cursor: pointer;
            padding: 0.25rem 0.5rem;
            text-transform: uppercase;
            color: var(--nav-link-color);
            transition: opacity 0.2s ease;
            font-size: var(--language-dropdown-font-size);
            font-weight: var(--language-dropdown-font-weight);
            letter-spacing: 0.02em;
            position: relative;
            text-decoration: none;
            display: inline-block;

            &.active {
              font-weight: 600;
              cursor: default;
              color: var(--nav-link-active-color);
              
              &::after {
                content: '';
                position: absolute;
                bottom: -2px;
                left: 50%;
                transform: translateX(-50%);
                width: 4px;
                height: 4px;
                background-color: currentColor;
                border-radius: 50%;
              }
            }

            &:hover:not(.active) {
              opacity: 0.7;
            }
          }

          .language-dropdown-toggle {
            background: none;
            border: none;
            cursor: pointer;
            padding: 0.25rem 0.5rem;
            text-transform: uppercase;
            color: var(--nav-link-color);
            transition: opacity 0.2s ease;
            font-size: var(--language-dropdown-font-size);
            font-weight: var(--language-dropdown-font-weight);
            letter-spacing: 0.02em;
            display: flex;
            align-items: center;
            gap: 0.375rem;

            svg {
              transition: transform 0.2s ease;
              transform: rotate(0deg);
            }

            &[aria-expanded="true"] svg {
              transform: rotate(180deg);
            }

            &:hover {
              opacity: 0.7;
            }
          }

          .language-dropdown {
            position: absolute;
            /* Position dropdown below the header:
               + 0.25rem = button's top padding
               + 0.375rem = button's bottom padding + small visual gap */
            top: calc(100% + var(--header-padding, 18px) + 0.25rem + var(--header-border-bottom-thickness, 1px) + 0.375rem);
            right: 0;
            background: var(--language-dropdown-inactive-bg-color);
            border: 1px solid var(--header-border-bottom-color);
            border-radius: var(--language-dropdown-border-radius);
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            z-index: 200;
            min-width: var(--language-dropdown-min-width);
            overflow: hidden;

            .language-option {
              display: block;
              padding: 0.5rem 0.75rem;
              text-decoration: none;
              color: var(--language-dropdown-inactive-color);
              background-color: var(--language-dropdown-inactive-bg-color);
              font-size: var(--language-dropdown-font-size);
              font-weight: var(--language-dropdown-font-weight);
              text-transform: uppercase;
              letter-spacing: 0.02em;
              transition: all 0.2s ease;
              border-bottom: 1px solid var(--header-border-bottom-color);

              &:last-child {
                border-bottom: none;
              }

              &.active {
                background-color: var(--language-dropdown-active-bg-color);
                color: var(--language-dropdown-active-color);
                font-weight: 600;
              }

              &:hover:not(.active) {
                background-color: var(--language-dropdown-hover-bg-color);
                color: var(--language-dropdown-hover-color);
              }
            }
          }
        }
      
      .search-toggle {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.5rem;
        -webkit-tap-highlight-color: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        
        svg {
          color: var(--icon-color);
          stroke: currentColor;
          -webkit-appearance: none;
          display: block;
          
          * {
            color: inherit;
            stroke: currentColor;
            -webkit-text-fill-color: currentColor;
          }
        }
      }

      .hamburger {
        background: none;
        border: none;
        padding: 15px;
        padding-right: 0;
        cursor: pointer;
        position: relative;
        z-index: 100;
        
        span {
          display: block;
          width: 14.4px;
          height: 3px;
          background-color: var(--hamburger-icon-color);
          position: relative;
          transition: all 0.3s ease;
          margin-left: auto;

          &::before,
          &::after {
            content: '';
            position: absolute;
            width: 24px;
            height: 3px;
            background-color: var(--hamburger-icon-color);
            right: 0;
            transition: all 0.3s ease;
          }

          &::before {
            top: -8px;
          }

          &::after {
            bottom: -8px;
          }

          &.open {
            background: transparent;

            &::before {
              transform: rotate(45deg);
              top: 0;
              width: 24px;
            }

            &::after {
              transform: rotate(-45deg);
              bottom: 0;
              width: 24px;
            }
          }
        }
      }
    }

    .mobile-header-actions {
      display: flex;
      align-items: center;
      gap: 1rem;
      height: 100%;
      margin-left: auto;

      @media screen and (min-width: 992px) {
        display: none;
      }

      .header-language-selector {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-right: 0.5rem;
        position: relative;

        .language-toggle {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          text-transform: uppercase;
          color: var(--nav-link-color);
          transition: opacity 0.2s ease;
          font-size: var(--language-dropdown-font-size);
          font-weight: var(--language-dropdown-font-weight);
          letter-spacing: 0.02em;
          position: relative;
          text-decoration: none;
          display: inline-block;

          &.active {
            font-weight: 600;
            cursor: default;
            color: var(--nav-link-active-color);
            
            &::after {
              content: '';
              position: absolute;
              bottom: -2px;
              left: 50%;
              transform: translateX(-50%);
              width: 4px;
              height: 4px;
              background-color: currentColor;
              border-radius: 50%;
            }
          }

          &:hover:not(.active) {
            opacity: 0.7;
          }
        }

        .language-dropdown-toggle {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          text-transform: uppercase;
          color: var(--nav-link-color);
          transition: opacity 0.2s ease;
          font-size: var(--language-dropdown-font-size);
          font-weight: var(--language-dropdown-font-weight);
          letter-spacing: 0.02em;
          display: flex;
          align-items: center;
          gap: 0.375rem;

          svg {
            transition: transform 0.2s ease;
            transform: rotate(0deg);
          }

          &[aria-expanded="true"] svg {
            transform: rotate(180deg);
          }

          &:hover {
            opacity: 0.7;
          }
        }

        .language-dropdown {
          position: absolute;
          /* Position dropdown below the header:
             100% = full height of header container
             + var(--header-padding, 18px) = header's vertical padding
             + 0.25rem = button's top padding (from padding: 0.25rem 0.5rem)
             + var(--header-border-bottom-thickness, 1px) = header's bottom border
             + 0.375rem = button's bottom padding + small visual gap */
          top: calc(100% + var(--header-padding, 18px) + 0.25rem + var(--header-border-bottom-thickness, 1px) + 0.375rem);
          right: 0;
          background: var(--language-dropdown-inactive-bg-color);
          border: 1px solid var(--header-border-bottom-color);
          border-radius: var(--language-dropdown-border-radius);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          z-index: 200;
          min-width: var(--language-dropdown-min-width);
          overflow: hidden;

          .language-option {
            display: block;
            padding: 0.5rem 0.75rem;
            text-decoration: none;
            color: var(--language-dropdown-inactive-color);
            background-color: var(--language-dropdown-inactive-bg-color);
            font-size: var(--language-dropdown-font-size);
            font-weight: var(--language-dropdown-font-weight);
            text-transform: uppercase;
            letter-spacing: 0.02em;
            transition: all 0.2s ease;
            border-bottom: 1px solid var(--header-border-bottom-color);

            &:last-child {
              border-bottom: none;
            }

            &.active {
              background-color: var(--language-dropdown-active-bg-color);
              color: var(--language-dropdown-active-color);
              font-weight: 600;
            }

            &:hover:not(.active) {
              background-color: var(--language-dropdown-hover-bg-color);
              color: var(--language-dropdown-hover-color);
            }
          }
        }
      }

      .search-toggle {
        background: none;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        
        svg {
          color: var(--icon-color);
          stroke: currentColor;
          -webkit-appearance: none;
          display: block;
          
          * {
            color: inherit;
            stroke: currentColor;
            -webkit-text-fill-color: currentColor;
          }
        }
      }

      .hamburger {
        background: none;
        border: none;
        padding: 15px;
        padding-right: 0;
        cursor: pointer;
        position: relative;
        z-index: 100;

        span {
          display: block;
          width: 14.4px;
          height: 3px;
          background-color: var(--hamburger-icon-color);
          position: relative;
          transition: all 0.3s ease;
          margin-left: auto;

          &::before,
          &::after {
            content: '';
            position: absolute;
            width: 24px;
            height: 3px;
            background-color: var(--hamburger-icon-color);
            right: 0;
            transition: all 0.3s ease;
          }

          &::before {
            top: -8px;
          }

          &::after {
            bottom: -8px;
          }

          &.open {
            background: transparent;

            &::before {
              transform: rotate(45deg);
              top: 0;
              width: 24px;
            }

            &::after {
              transform: rotate(-45deg);
              bottom: 0;
              width: 24px;
            }
          }
        }
      }
    }
  }

  /* Overlay Menu Styles */
  .overlay-backdrop {
    position: fixed;
    top: var(--header-height, 60px);
    left: 0;
    width: 100vw;
    height: calc(100vh - var(--header-height, 60px));
    background: rgba(0, 0, 0, 0.5);
    z-index: 140;
    border: none;
    padding: 0;
    cursor: pointer;
    
    @media screen and (max-width: 991px) {
      display: none; /* Hide backdrop on mobile */
    }
  }

  .overlay-menu {
    position: fixed;
    top: var(--header-height, 60px);
    background: var(--overlay-menu-background-color);
    z-index: 150;
    
    &.desktop {
      right: 0;
      width: 50vw;
      height: calc(100vh - var(--header-height, 60px));
      overflow-y: auto;
      transform-origin: right center;
      will-change: transform;
      
      /* Desktop-specific styles */
      .overlay-menu-content {
        height: 100%;
        padding-bottom: 0;
      }
    }
    
    &.mobile {
      width: 100%;
      left: 0;
      height: auto;
      bottom: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: auto;
    }
    
    @media screen and (max-width: 991px) {
      width: 100%;
      left: 0;
    }
  }

  .overlay-menu-content {
    display: flex;
    flex-direction: column;
    padding-bottom: 30px; /* Only for mobile */
  }

  .overlay-nav {
    flex: 1;
    padding: 2rem;
    
    .overlay-nav-links {
      list-style: none;
      padding: 0;
      margin: 0;
      
      .overlay-nav-item {
        margin-bottom: 1.5rem;
        
        &.submenu-item {
          padding-left: 20px;
          
          a {
            font-size: calc(var(--adjusted-font-size, var(--nav-link-font-size)) - 2px);
            font-weight: 500;
          }
        }
        
        a {
          display: block;
          text-decoration: none;
          color: var(--overlay-menu-link-color, #424242);
          text-transform: uppercase;
          font-size: var(--adjusted-font-size, var(--nav-link-font-size));
          font-weight: 600;
          line-height: 30px;
          letter-spacing: 1px;
          transition: color 0.2s ease, opacity 0.2s ease;
          padding: 5px 0;
          white-space: nowrap;

          &.active {
            color: var(--overlay-menu-link-active-color, #424242);
            text-decoration: underline;
            text-underline-offset: 8px;
            text-decoration-thickness: 2px;
          }

          &:hover {
            color: var(--overlay-menu-link-hover-color, #202020);
            opacity: 0.7;
          }
        }
      }
      
      .overlay-nav-item.secondary {
        margin-bottom: 1.5rem;
        
        a {
          font-size: calc(var(--adjusted-font-size, var(--nav-link-font-size)) - 2px); // Fixed smaller font size
          font-weight: 500;
          line-height: 24px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--nav-link-color);

          &.active {
            color: var(--nav-link-active-color);
            text-decoration: underline;
            text-underline-offset: var(--nav-link-underline-offset);
            text-decoration-thickness: var(--nav-link-underline-thickness);
            text-decoration-color: var(--nav-link-underline-color);
          }

          &:hover {
            color: var(--nav-link-hover-color);
            opacity: 0.7;
          }
        }
      }
    }
    
    .secondary-nav-divider {
      margin: var(--secondary-nav-divider-vertical-margin, 20px) var(--secondary-nav-divider-horizontal-margin, 0);
      height: var(--secondary-nav-divider-height, 1px);
      background-color: var(--secondary-nav-divider-bg-color, rgba(0, 0, 0, 0.1));
      width: var(--secondary-nav-divider-width, 50px);
      opacity: var(--secondary-nav-divider-opacity, 0.25);
    }

    .overlay-nav-links.secondary {
      margin-top: 10px;
    }
  }

  .overlay-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-top: 1px solid #E5E5E5;
    margin-top: 0.5rem;
    background-color: var(--overlay-menu-background-color);
    color: var(--overlay-menu-link-color, #424242);
    
    .today-hours {
      font-size: 16px;
      letter-spacing: 1px;
      padding-right: 4rem;
      color: inherit;
    }

    .language-selector {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      color: inherit;
    }

    .language-toggle {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.5rem;
      text-transform: uppercase;
      color: inherit;
      transition: opacity 0.2s ease;
      font-size: 16px;
      font-weight: 500;
      letter-spacing: 0.02em;
      position: relative;
      text-decoration: none;
      display: inline-block;

      &.active {
        font-weight: 600;
        cursor: default;
        
        &::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 50%;
          transform: translateX(-50%);
          width: 5px;
          height: 5px;
          background-color: currentColor;
          border-radius: 50%;
        }
      }

      &:hover:not(.active) {
        opacity: 0.7;
      }
    }
  }

  .search-overlay {
    position: absolute;
    top: 100%;
    right: 0;
    width: 500px;
    background: var(--search-overlay-background-color, #FFFFFF);
    padding: 0.5rem 1rem;
    z-index: 100;

    @media screen and (max-width: 991px) {
      width: 100%;
      padding: 0.5rem 0;
      background: var(--search-overlay-background-color, #FFFFFF);
      z-index: 101;
    }
  }

  .search-container {
    margin: 0 auto;
    padding: 0;
    display: flex;
    justify-content: flex-end;
    position: relative;
    z-index: 101;
    
    @media screen and (max-width: 991px) {
      justify-content: stretch;
      background: var(--search-overlay-background-color, #FFFFFF);
      padding: 0.5rem 1rem;
      width: 100%;
    }
  }

  .search-backdrop {
    display: none;
    cursor: pointer;
    
    @media (max-width: 991px) {
      display: block;
      position: fixed;
      top: var(--header-height, 60px);
      left: 0;
      background: rgba(0, 0, 0, 0.3);
      height: calc(100vh - var(--header-height, 60px));
      width: 100vw;
      z-index: 99;
    }
  }

  @media (max-width: 991px) {
    .search-overlay {
      position: absolute;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      width: 100%;
      padding: 0;
      z-index: 100;
      background: var(--search-overlay-background-color, #FFFFFF);
    }
  }

  :global(html.demo2025-search-open),
  :global(html.demo2025-menu-open) {
    overflow: hidden;
  }
  
  :global(html.demo2025-search-open body),
  :global(html.demo2025-menu-open body) {
    overflow: hidden;
    position: relative;
  }

  @media screen and (max-width: 991px) {
    .overlay-menu {
      transition: opacity 0.3s ease;
    }
  }

  /* Styling for dropped fragments */
  :global([slot="dropped-fragments"]) {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    @media screen and (max-width: 991px) {
      gap: 0.5rem;
    }
  }
</style> 